// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { PopoverContextProps } from '@iheartjane/reefer';
import {
  Button,
  CheckIcon,
  ChevronDownIcon,
  Flex,
  List,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import { darken } from 'polished';
import { useCallback } from 'react';

import type { Kiosk, Store } from '@jane/shared/types';

import { useKioskSpacing } from '../../../hooks';
import {
  useComplianceLanguage,
  useKioskJaneModeContext,
} from '../../../providers';
import { capitalizeEveryWord } from '../../../util';

export const StyledButton = styled(Button)(({ theme }) => {
  const { getKioskSpacing } = useKioskSpacing();
  const isJaneMode = useKioskJaneModeContext();
  const paddingY = getKioskSpacing(4, 2);
  const color = darken(
    0.05,
    isJaneMode ? theme.colors.grays.ultralight : theme.colors.primary.main
  );

  return {
    border: 'unset',
    backgroundColor: color,
    borderRadius: 32,
    height: 'auto',
    padding: `${paddingY}px 8px ${paddingY}px 12px`,
  };
});

export interface StoreSwitcherProps {
  currentStore: Store;
  onClick: () => void;
  options: Kiosk['stores'];
}

export const StoreSwitcher = ({
  options,
  currentStore,
  onClick,
}: StoreSwitcherProps) => {
  const { isIpad } = useKioskSpacing();
  const isJaneMode = useKioskJaneModeContext();
  const { getWord } = useComplianceLanguage();

  const onStoreSelected = useCallback(
    (
      closePopover: PopoverContextProps['closePopover'],
      recreational: boolean
    ) => {
      closePopover();
      if (currentStore.recreational !== recreational) {
        onClick();
      }
    },
    [currentStore.recreational, onClick]
  );

  return (
    <Popover
      disableMobileStyling
      label="Select store"
      mr="auto"
      target={
        <StyledButton
          endIcon={
            <ChevronDownIcon
              size="sm"
              color={isJaneMode ? 'grays-mid' : 'text-info'}
            />
          }
          label={
            <Typography
              variant="mini-bold"
              color={isJaneMode ? 'grays-mid' : 'text-info'}
            >
              {capitalizeEveryWord(
                currentStore.recreational
                  ? (getWord('recreational') ?? 'Recreational')
                  : 'Medical'
              )}
            </Typography>
          }
          size="small"
          data-testid="store-switcher-btn"
          variant="tertiary"
        />
      }
    >
      {({ closePopover }: PopoverContextProps) => (
        <Popover.Content padding={false}>
          <List label="store selector" py={isIpad ? 8 : 16}>
            {options?.map(({ recreational, name }) => (
              <List.Item
                ariaLabel={name}
                onClick={() => onStoreSelected(closePopover, recreational)}
                key={`${name} ${recreational}`}
                px={24}
                py={12}
              >
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection="row"
                >
                  <Typography>
                    {capitalizeEveryWord(
                      recreational
                        ? (getWord('recreational') ?? 'Recreational')
                        : 'Medical'
                    )}
                  </Typography>
                  {currentStore.recreational === recreational && <CheckIcon />}
                </Flex>
              </List.Item>
            ))}
          </List>
        </Popover.Content>
      )}
    </Popover>
  );
};
